import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const client = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
});

export type TranscriptRole = "user" | "assistant";

export interface TranscriptTurn {
  role: TranscriptRole;
  content: string;
  timestamp: string;
  audioBlobUrl?: string;
  error?: {
    type: 'api_error' | 'speech_error' | 'upload_error';
    message: string;
    details?: string;
  };
}

export interface ScenarioInfo {
  id: string;
  title: string;
}

export interface TranscriptDocument {
  sessionId: string;
  scenario?: ScenarioInfo;
  turns: TranscriptTurn[];
  createdAt: string;
  updatedAt: string;
}

export interface UploadAudioResponse {
  sessionId: string;
  blobUrl: string;
  blobName: string;
}

// Chat API types
export interface ChatMessage {
  role: "system" | "user" | "assistant";
  content: string;
}

export interface ChatResponse {
  content: string;
  finishReason?: string;
}

// Speech API types
export interface SpeechToTextResult {
  transcript: string;
  confidence?: number;
}

export const uploadSessionAudio = async (
  sessionId: string,
  file: Blob,
  fileName = "audio.wav"
): Promise<UploadAudioResponse> => {
  const formData = new FormData();
  formData.append("sessionId", sessionId);
  formData.append("audio", file, fileName);

  const { data } = await client.post<UploadAudioResponse>(
    "/uploadAudio",
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
    }
  );

  return data;
};

export const saveTranscript = async (
  sessionId: string,
  turns: TranscriptTurn[],
  scenario?: ScenarioInfo
): Promise<void> => {
  await client.post<TranscriptDocument>("/saveTranscript", {
    sessionId,
    scenario,
    turns,
  });
};

export const getTranscript = async (
  sessionId: string
): Promise<TranscriptDocument | null> => {
  try {
    const { data } = await client.get<TranscriptDocument>("/getTranscript", {
      params: { sessionId },
    });
    return data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

export const getAllTranscripts = async (): Promise<TranscriptDocument[]> => {
  try {
    const { data } = await client.get<TranscriptDocument[]>("/getAllTranscripts");
    return data;
  } catch (error) {
    console.error("Error fetching all transcripts:", error);
    throw error;
  }
};

// New Chat API
export const getChatCompletion = async (
  messages: ChatMessage[]
): Promise<ChatResponse> => {
  const { data } = await client.post<ChatResponse>("/chat", {
    messages,
  });
  return data;
};

// New Speech APIs
export const textToSpeech = async (text: string, voiceName?: string): Promise<Blob> => {
  const response = await client.post("/speech/text-to-speech", 
    { text, voiceName },
    {
      responseType: "blob",
      headers: {
        "Accept": "audio/mpeg, audio/wav",
      },
    }
  );
  
  return new Blob([response.data], { 
    type: response.headers["content-type"] || "audio/mpeg" 
  });
};

export const speechToText = async (audioFile: File | Blob): Promise<SpeechToTextResult> => {
  const formData = new FormData();
  formData.append("audio", audioFile, "audio.wav");

  const { data } = await client.post<SpeechToTextResult>(
    "/speech/speech-to-text",
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
    }
  );

  return data;
};
